import { useState } from 'react';
import type { Servicio } from '../api/tipos.js';
import { BotonAtras, EstadoVacio } from '../componentes/comunes.js';
import { ICONOS_PRODUCTO } from '../logos.js';
import { nombreDeProducto } from '../nombres.js';
import { textos } from '../textos.js';

/**
 * Paso 3: elegir el software (§9).
 *
 * Solo se llega aquí cuando el cliente tiene **más de un** servicio contratado:
 * con uno solo, el servidor manda saltar el paso (D-03).
 */
export function PantallaServicio({
  servicios,
  alElegir,
  alVolver,
}: {
  servicios: Servicio[];
  alElegir: (servicio: Servicio) => void;
  alVolver: () => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const termino = normalizar(busqueda);
  const filtrados = servicios.filter((servicio) =>
    normalizar(
      `${servicio.nombre} ${nombreDeProducto(servicio.nombre)} ${servicio.codigo}`,
    ).includes(termino),
  );

  return (
    <section className="pantalla-servicio" aria-labelledby="titulo-servicio">
      <BotonAtras alClick={alVolver} />
      <h2 id="titulo-servicio">{textos.servicio.titulo}</h2>
      <p className="ayuda">{textos.servicio.ayuda}</p>

      {servicios.length > 4 && (
        <label className="buscador">
          <span>{textos.servicio.buscar}</span>
          <span className="control-buscador">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m16 16 4 4" />
            </svg>
            <input
              type="search"
              value={busqueda}
              placeholder={textos.servicio.buscarPlaceholder}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />
          </span>
        </label>
      )}

      {servicios.length === 0 && <EstadoVacio titulo={textos.servicio.sinServicios} compacto />}

      {servicios.length > 0 && filtrados.length === 0 && (
        <EstadoVacio
          titulo={textos.servicio.sinResultados}
          detalle={textos.servicio.sinResultadosAyuda}
          compacto
        />
      )}

      {filtrados.length > 0 && (
        <ul className="opciones">
          {filtrados.map((servicio) => (
            <li key={servicio.id}>
              <button type="button" className="opcion" onClick={() => alElegir(servicio)}>
                <IconoServicio codigo={servicio.codigo} />
                <span className="texto-opcion">
                  <span className="nombre">{nombreDeProducto(servicio.nombre)}</span>
                  {servicio.descripcion !== null && (
                    <span className="detalle">{servicio.descripcion}</span>
                  )}
                  <span className="detalle duracion-opcion">
                    {textos.servicio.duracion(servicio.duracionMin)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="botonera">
        <button type="button" className="enlace" onClick={alVolver}>
          {textos.aplicacion.volver}
        </button>
      </div>
    </section>
  );
}

function normalizar(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es');
}

/** El icono del producto, o el genérico si no lo hay. Es decorativo: el nombre va al lado. */
function IconoServicio({ codigo }: { codigo: string }) {
  const [fallaElIcono, setFallaElIcono] = useState(false);
  const fichero = ICONOS_PRODUCTO[codigo];

  if (fichero !== undefined && !fallaElIcono) {
    return (
      <img
        className="icono-producto"
        src={`/productos/${fichero}`}
        alt=""
        onError={() => setFallaElIcono(true)}
      />
    );
  }

  return (
    <span className="icono-opcion servicio" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 21h8m-4-4v4m-5-9 3-3 2 2 4-4 2 2" />
      </svg>
    </span>
  );
}
